"use client";

import type {
	Language,
	Locale,
	StaticSiteConfig,
} from "@commerce-shared/site-config";
import type { PropsWithChildren } from "react";
import { createContext, useContext } from "react";
import type { IconName } from "../../icons.generated";

export type CompanyLinks = {
	business?: {
		link: { url: string };
		label: string;
		icon?: IconName;
	};
	service?: {
		link: { url: string };
		label: string;
		icon?: IconName;
	};
	terms?: {
		link: { url: string };
		label: string;
	};
	vacancies?: {
		link: string;
		label: string;
	};
	businessTerms?: {
		link: { url: string };
	};
	privacy?: {
		link: { url: string };
		label: string;
	};
};

const StaticSiteConfigContext = createContext<
	| (StaticSiteConfig & { companyLinks?: CompanyLinks } & {
			language: Language;
			locale: Locale;
	  })
	| null
>(null);

type Props = PropsWithChildren<{
	config: StaticSiteConfig;
	language: Language;
	companyLinks?: CompanyLinks;
}>;

export const StaticSiteConfigProvider = ({
	children,
	config,
	companyLinks,
	language,
}: Props) => (
	<StaticSiteConfigContext.Provider
		value={{
			...config,
			companyLinks,
			language,
			locale: `${language}-${config.country}` as Locale,
		}}
	>
		{children}
	</StaticSiteConfigContext.Provider>
);

export const useStaticSiteConfig = () => {
	const config = useContext(StaticSiteConfigContext);

	if (!config) {
		throw new Error(
			"storeConfig is null: create a configuration of type StaticSiteConfig and pass to StaticSiteConfigProvider",
		);
	}

	return config;
};
